// Insert (FR-006) and rename (FR-006a) a placeholder.
//
// In insert mode this is an editable combobox (research.md D-14): it lists the
// fields the template already contains so a repeat use is *selected*, not retyped
// (FR-023), while still accepting a name nobody has used before (FR-026). The
// list is the exact set extraction already derives, so selecting a listed field
// can never grow the template's distinct-field count (FR-025, SC-013).
//
// The name is validated against the shared pattern *before* insertion, so an
// unnameable placeholder never enters the document — the server's rejection
// (FR-008) is the backstop, not the primary feedback.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type JSX,
  type KeyboardEvent,
} from 'react';
import {
  PLACEHOLDER_NAME_MAX_LENGTH,
  findConfusableName,
  isValidPlaceholderName,
} from '@template-filler/core';
import { containsFold } from '../lib/search.js';

interface PlaceholderDialogProps {
  mode: 'insert' | 'rename';
  initialName?: string;
  /** Distinct field names already in the template being edited (FR-023, FR-033). */
  existingNames: readonly string[];
  onConfirm: (name: string) => void;
  onCancel: () => void;
}

const LISTBOX_ID = 'placeholder-existing-fields';
const optionId = (index: number): string => `placeholder-option-${index}`;

export function PlaceholderDialog({
  mode,
  initialName = '',
  existingNames,
  onConfirm,
  onCancel,
}: PlaceholderDialogProps): JSX.Element {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | undefined>();
  // Index into the *filtered* option list of the highlighted option, or
  // undefined so that Enter takes the typed text (data-model.md invariants).
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus management: opening a dialog without moving focus into it strands
  // keyboard users on the page behind.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const isInsert = mode === 'insert';

  // The existing-field list is an insert-time affordance (D-17): renaming keeps
  // its single-input form and only inherits the near-duplicate warning below.
  const hasList = isInsert && existingNames.length > 0;

  const options = useMemo(
    () => (hasList ? existingNames.filter((candidate) => containsFold(candidate, name)) : []),
    [hasList, existingNames, name],
  );
  const listVisible = options.length > 0;

  // Renaming a field onto a name that only *looks* like another splits one field
  // into two silently (FR-030). The field being renamed is itself in the
  // document, so it must be excluded — otherwise every case change would warn
  // against itself.
  const comparisonNames = useMemo(
    () => (isInsert ? existingNames : existingNames.filter((n) => n !== initialName)),
    [isInsert, existingNames, initialName],
  );
  // Compare the raw typed name, not a trimmed copy: surrounding whitespace is a
  // confusable difference (FR-030, data-model.md), and an exact match is
  // short-circuited inside findConfusableName so identical names never warn.
  const confusable = findConfusableName(name, comparisonNames);

  function confirmName(chosen: string): void {
    onConfirm(chosen);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();

    // A highlighted option wins over the typed text (contract keyboard map).
    if (activeIndex !== undefined && options[activeIndex] !== undefined) {
      confirmName(options[activeIndex]);
      return;
    }

    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('Give the field a name so it can be filled in later.');
      return;
    }
    if (!isValidPlaceholderName(trimmed)) {
      setError(
        `Use letters, numbers, spaces, hyphens, or underscores, starting with a letter or number (up to ${PLACEHOLDER_NAME_MAX_LENGTH} characters).`,
      );
      return;
    }

    confirmName(trimmed);
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (options.length === 0) return; // arrows are inert with nothing to move through
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) =>
        current === undefined ? 0 : Math.min(current + 1, options.length - 1),
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) =>
        current === undefined ? options.length - 1 : Math.max(current - 1, 0),
      );
    }
  }

  const describedBy =
    [
      'placeholder-name-hint',
      error ? 'placeholder-name-error' : undefined,
      confusable ? 'placeholder-name-warning' : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className="dialog-backdrop" role="presentation" onClick={onCancel}>
      <form
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="placeholder-dialog-title"
        onClick={(event) => event.stopPropagation()}
        onSubmit={submit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel();
        }}
      >
        <h2 id="placeholder-dialog-title">{isInsert ? 'Insert a field' : 'Rename this field'}</h2>

        <label htmlFor="placeholder-name">Field name</label>
        <input
          id="placeholder-name"
          ref={inputRef}
          value={name}
          maxLength={PLACEHOLDER_NAME_MAX_LENGTH}
          role={isInsert ? 'combobox' : undefined}
          aria-expanded={isInsert ? listVisible : undefined}
          aria-controls={listVisible ? LISTBOX_ID : undefined}
          aria-autocomplete={isInsert ? 'list' : undefined}
          aria-activedescendant={activeIndex !== undefined ? optionId(activeIndex) : undefined}
          onChange={(event) => {
            setName(event.target.value);
            setError(undefined);
            // The filtered list is a different list; a carried-over index would
            // highlight an unrelated field (data-model.md invariant 2).
            setActiveIndex(undefined);
          }}
          onKeyDown={onInputKeyDown}
          aria-describedby={describedBy}
          aria-invalid={error !== undefined}
        />

        <p id="placeholder-name-hint" className="hint">
          This name becomes the form label when the template is filled in.
          {isInsert ? '' : ' Every use of this field will be renamed.'}
        </p>

        {isInsert && existingNames.length === 0 ? (
          <p className="hint" id="placeholder-empty">
            This template has no fields yet. Type a name to create the first one.
          </p>
        ) : null}

        {hasList && listVisible ? (
          <ul className="combobox-list" role="listbox" id={LISTBOX_ID} aria-label="Existing fields">
            {options.map((optionName, index) => (
              <li
                key={optionName}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                className={index === activeIndex ? 'combobox-option is-active' : 'combobox-option'}
                // preventDefault on mousedown keeps focus in the input (so typing
                // still filters); click performs the selection and is what the
                // component tests drive.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => confirmName(optionName)}
              >
                {optionName}
              </li>
            ))}
          </ul>
        ) : null}

        {hasList && !listVisible ? (
          <p className="hint" id="placeholder-no-match" role="status">
            No existing field matches “{name.trim()}”. Press Insert to create it as a new field.
          </p>
        ) : null}

        {confusable ? (
          <div id="placeholder-name-warning" className="warning" role="status">
            <p>
              “{confusable}” already exists and differs only by capitalisation or spacing.
              Continuing with a different capitalisation creates a second, separate field — select
              the existing one to reuse it.
            </p>
            <button type="button" onClick={() => confirmName(confusable)}>
              Use existing field “{confusable}”
            </button>
          </div>
        ) : null}

        {error ? (
          <p id="placeholder-name-error" className="error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="dialog-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {isInsert ? 'Insert' : 'Rename'}
          </button>
        </div>
      </form>
    </div>
  );
}
